/**
 * Cookie management utilities
 *
 * Handles consent state and tracking cookie setup.
 * GDPR-compliant: no tracking cookies set until user consents.
 *
 * API_TODO: When backend is wired, sync consent preferences via
 * POST /api/users/me/consent
 */

const CONSENT_KEY = "ck_cookie_consent";
const CONSENT_VERSION = "1"; // Bump when policy changes to re-prompt

/**
 * Get the current consent state
 * @returns {{ essential: boolean, analytics: boolean, marketing: boolean, version: string } | null}
 */
export function getConsentState() {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Re-prompt if consent version is stale
    if (parsed.version !== CONSENT_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

/**
 * Save consent preferences
 * @param {{ essential: boolean, analytics: boolean, marketing: boolean }} prefs
 */
export function saveConsent(prefs) {
  const state = {
    ...prefs,
    essential: true, // Always true — required for the site to function
    version: CONSENT_VERSION,
    timestamp: new Date().toISOString(),
  };
  localStorage.setItem(CONSENT_KEY, JSON.stringify(state));

  // Apply or remove tracking cookies based on consent
  if (state.analytics) {
    setTrackingCookie("ck_analytics", generateSessionId(), 365);
    // API_TODO: Initialize analytics SDK (e.g., Plausible, PostHog)
  } else {
    removeTrackingCookie("ck_analytics");
  }

  if (state.marketing) {
    setTrackingCookie("ck_marketing", generateSessionId(), 365);
    // API_TODO: Initialize marketing pixels if any
  } else {
    removeTrackingCookie("ck_marketing");
  }
}

/**
 * Accept all cookies
 */
export function acceptAllCookies() {
  saveConsent({ essential: true, analytics: true, marketing: true });
}

/**
 * Reject optional cookies (essential only)
 */
export function rejectOptionalCookies() {
  saveConsent({ essential: true, analytics: false, marketing: false });
}

// ── Internal helpers ──

function setTrackingCookie(name, value, days) {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${value}; expires=${expires}; path=/; SameSite=Lax; Secure`;
}

function removeTrackingCookie(name) {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
}

function generateSessionId() {
  return "ck_" + Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
}
